import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Button, Container, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import { ReactComponent as ConnectionIcon } from "../../assets/icons/settings-outlined.svg";
import DashboardsTableProvider from "../../contexts/TablesContext/DashboardsTableProvider";
import { scrollbarStyle } from "../../shared/dashboard";
import UnassignedContentsBadge from "../atoms/UnassignedContentsBadge";
import AddEditCompanyModal from "../modals/companies/AddEditCompanyModal";
import ManageFieldsLoaderModal from "../modals/home-manage-fields/ManageFieldsLoaderModal";
import PlanModal from "../modals/subscriptions/PlanModal";
import DashboardsSearchOrCreate from "../molecules/dashboards-search-create/DashboardsSearchOrCreate";
import DashboardsTableFilter from "../molecules/dashboards-table/DashboardsTableFilter";
import PipelineInsights from "../molecules/home-overview/PipelineInsights";
import configuredAsyncComponentLoader from "../templates/loader";
import useDashboardsTable from '../../hooks/useDashboardsTable';

const Companies = configuredAsyncComponentLoader(() => import('../organisms/home/Companies'));

export enum ViewModes {
    All = '',
    NewDeals = 'new-deals',
    CompanyUpdates = 'company-updates',
    NewFundOpportunities = 'new-fund-opportunities',
    FundPortfolioUpdates = 'fund-portfolio-updates',
}

const useStyles = makeStyles((theme) => ({
    rootContainer: {
        height: '100%',
        maxWidth: 'unset !important',
        overflow: 'hidden',
        padding: 'unset',
    },

    tabs: {
        "& .MuiTabs-indicator": {
            height: 3,
            background: theme.colors.primary['700'],
        }
    },
    tab: {
        minWidth: 100,
        minHeight: 48,
        marginRight: 8,
        padding: '6px 8px',
    },
    tabText: {
        color: '#878E95',
        fontFamily: 'Lato',
        fontWeight: 'bold',
        textTransform: 'none',
    },
    selectedTabText: {
        color: 'black',
        fontFamily: 'Lato',
        fontWeight: 'bold',
    },

    mainText: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.success['500'],
    },
    subText: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 500,
        color: '#666666',
    },


    block: {
        width: '100%',
        height: '100%',
        transition: '0.5s ease-in-out',
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.2rem !important',
        fontWeight: 600,
        color: theme.colors.neutral['700'],
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: '1.1rem !important',
        fontWeight: 600,
        color: theme.colors.neutral['600'],
    },

    activity: {
        height: 'calc(100vh - 120px)',
        background: 'white',
        overflow: 'hidden',
        padding: 1,
    },
    tableView: {
        position: 'relative',
        width: '100%',
        minWidth: 'calc(100vw - 94px - 500px)',
        maxWidth: 'calc(100vw - 94px - 500px)',
        height: 'calc(100vh - 200px)',
        overflow: 'auto',
        ...scrollbarStyle,
    },

    link: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.palette.primary.light,
        cursor: 'pointer',
    },
    createButton: {
        width: 180,
        height: 45,
        borderRadius: 16,
        borderColor: theme.colors.neutral['100'],
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    manageButton: {
        width: 180,
        height: 45,
        borderRadius: 16,
        borderColor: '#e5e7eb',
        color: '#334155',
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
        "&:hover": {
            borderColor: '#334155',
            background: '#f3f4f6',
        },
    },
    manageIcon: {
        width: 20,
        height: 20,
    },
    formContainer: {
        height: 'auto',
        width: 'auto',
        paddingTop: '8px',
        paddingBottom: '8px',
        marginLeft: '8px',
        "& > .MuiInputBase-root": {
            justifyContent: 'center',
            fontFamily: 'Inter',
            color: '#666666',
            "& > div": {
                backgroundColor: 'unset !important',
            },
            "&::before": {
                content: 'unset !important',
            },
            "&::after": {
                content: 'unset !important',
            }
        }
    },
}));

const DashboardsSearch: React.FC<{ setSearch: React.Dispatch<React.SetStateAction<string>> }> = ({ setSearch }) => {
	const { rows : dashboards } = useDashboardsTable();

    return (<DashboardsSearchOrCreate
        dashboards={dashboards}
        search squareFit clear
        onSearch={(value) => setSearch(value)} />);
}

const DashboardsPageV2: React.FC<{}> = () => {
    const classes = useStyles();
    const params: {[key: string]: string} = useParams();
    const [search, setSearch] = useState<string>('');
    const [expanded, setExpanded] = useState<boolean>(false);
    const [addCompanyModalOpen, setAddCompanyModalOpen] = useState<boolean>(false);
    const [manageFieldsModalOpen, setManageFieldsModalOpen] = useState<boolean>(false);
    const [companiesCount, setCompaniesCount] = useState<number|undefined>(undefined);
    const [mode, setMode] = useState<string>(ViewModes.All);

    const sidebarHeight = 'calc(100vh - 140px)';

    useEffect(() => {
        const view = (params.view || '').trim();

        if ((Object.values(ViewModes) as string[]).includes(view)) {
            if (view === ViewModes.NewDeals) {
                setMode('New deal');
            } else if (view === ViewModes.CompanyUpdates) {
                setMode('Company update');
            } else if (view === ViewModes.NewFundOpportunities) {
                setMode('New fund opportunity');
            } else if (view === ViewModes.FundPortfolioUpdates) {
                setMode('Fund portfolio update');
            }
        }
    }, [params.view]);

    return (<>
        <Container className={classes.rootContainer}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" ml={3}>
                    <Stack alignItems="center" justifyContent="center" p={2} width={50} height={50}
                        sx={{ borderRadius: 4, background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)' }}>
                        <Typography fontSize="1.4rem !important" fontWeight="bold" fontFamily="Inter" color="#fff">
                            {'N'}
                        </Typography>
                    </Stack>
                    <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" >
                        <Typography fontFamily="Inter" fontWeight="bold" fontSize="1.5rem !important" textTransform="capitalize">
                            {(params.view || '').trim() === ViewModes.NewFundOpportunities 
                                ? 'New Fund Opportunities' : (mode ? mode.concat('s') : 'Your Pipeline')}
                        </Typography>
                        <Typography fontSize="0.9rem" fontWeight="400" color="#64748b">
                            {'Manage your deals and opportunities'}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" mr={2}>
                    <UnassignedContentsBadge />
                </Stack>
            </Stack>
            <Stack direction="row" alignItems="flex-start" width="100%" height="100%">
                <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="flex-start"
                    width={expanded ? 'calc(100vw - 96px - 500px)' : '96%'} height="100%" pt={2}>
                    <DashboardsTableProvider>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" pr={3} pl={3}>
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start" width="100%">
                                <DashboardsSearch setSearch={setSearch} />
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                                    <Button variant="outlined"
                                        className={classes.createButton}
                                        startIcon={<AddIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setAddCompanyModalOpen(true);
                                        }} disableRipple>{'Add company'} </Button>
                                    <Button variant="outlined"
                                        className={classes.manageButton}
                                        startIcon={<ConnectionIcon className={classes.manageIcon}  />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            setManageFieldsModalOpen(true);
                                        }} disableRipple>{'Customize'} </Button>
                                </Stack>
                            </Stack>
                            <DashboardsTableFilter count={companiesCount} recentType={mode} />
                        </Stack>
                        <Companies search={search} expanded={expanded} preFilter={{ recent: [mode] }} onCount={setCompaniesCount} noTabs />
                    </DashboardsTableProvider>
                </Stack>
                {expanded ? (<>
                    <Divider orientation="vertical" sx={{ height: sidebarHeight, paddingLeft: 2 }} />
                    <Stack className={classes.activity} width="100%" height={sidebarHeight} p={1}>
                        <PipelineInsights onExpand={setExpanded} />
                    </Stack>
                </>) : (<>
                    <Divider orientation="vertical" sx={{ height: sidebarHeight }} />
                    <Stack direction="column" className={classes.activity} spacing={3} alignItems="center" mt={2} px={3}
                        sx={{ width: 72, height: '100%', cursor: 'pointer'}}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(true);
                        }}>
                        <IconButton onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(true);
                        }}> <ArrowBackIosIcon /> </IconButton>
                        <Tooltip placement="left" title={
                                <Typography fontFamily="Inter">
                                    {'Pipeline Insights'}
                                </Typography>
                            } componentsProps={{tooltip:{sx:{
                                display: 'flex', alignItems: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                height: '28px', padding: "20px",
                            }}}}>
                            <TrendingUpIcon sx={{ fill: 'rgba(0, 0, 0, 0.5)' }} />
                        </Tooltip>
                    </Stack>
                </>)}
            </Stack>
        </Container>
        <PlanModal variant="limit" />
        {addCompanyModalOpen && (
            <AddEditCompanyModal
                isOpen={addCompanyModalOpen}
                onClose={() => setAddCompanyModalOpen(false)}
            />
        )}
        {manageFieldsModalOpen && (
            <ManageFieldsLoaderModal
                open={manageFieldsModalOpen}
                recentType={mode}
                onClose={() => setManageFieldsModalOpen(false)}
            />
        )}
    </>);
}

export default DashboardsPageV2;
